/**
 * Port of C# LoaderJsonHelper.cs
 * Shared helper utilities for JSON loaders
 * Contains common JSON serialization and utility methods used by both LoaderNormalJson and LoaderPreProcessJson
 */

import { JsonUtility } from '../../../../Assembler/dist/app/jsonUtility.js';
import { JsonObject, JsonValue } from '../../../../Assembler/dist/app/json/jsonValue.js';

// #region JSON Serialization

/**
 * Converts a simple JSON value (string, numeric, bool, null) to string.
 * Complex nested structures are serialized as strings via toString().
 */
export function simpleValueToString(value: unknown): string {
    if (value === null || value === undefined) {
        return 'null';
    }

    if (typeof value === 'string') {
        return '"' + JsonUtility.escapeJson(value) + '"';
    }

    if (typeof value === 'boolean') {
        return value ? 'true' : 'false';
    }

    if (typeof value === 'number') {
        return value.toString();
    }

    // Fallback: serialize using toString() as JSON string
    return '"' + JsonUtility.escapeJson((value as object).toString()) + '"';
}

export function appendJsonValue(sb: string[], value: JsonValue | unknown, maskHtml: boolean = false): void {
    if (value instanceof JsonValue) {
        if (value.isNull) {
            sb.push('null');
            return;
        }
        if (value.isString) {
            const text = value.getString();
            sb.push('"');
            sb.push(JsonUtility.escapeJson(maskHtml ? text.replace(/./g, '*') : text));
            sb.push('"');
            return;
        }
        if (value.isBool) {
            sb.push(value.getBool() ? 'true' : 'false');
            return;
        }
        if (value.isNumber) {
            sb.push(String(value.getNumber()));
            return;
        }
        if (value.isInteger) {
            sb.push(String(value.getInteger()));
            return;
        }
        if (value.isObject) {
            appendDictionaryAsJsonObject(sb, value.getObject(), maskHtml);
            return;
        }
        if (value.isArray) {
            sb.push('[');
            const arr = value.getArray();
            for (let i = 0; i < arr.length; i++) {
                if (i > 0) sb.push(',');
                appendJsonValue(sb, arr[i], maskHtml);
            }
            sb.push(']');
            return;
        }
    }

    sb.push(simpleValueToString(value));
}

export function appendDictionaryAsJsonObject(sb: string[], dict: JsonObject | null, maskHtml: boolean = false): void {
    if (dict == null) {
        sb.push('null');
        return;
    }

    sb.push('{');
    const entries = Array.from(dict.entries());
    for (let i = 0; i < entries.length; i++) {
        if (i > 0) sb.push(',');
        const [key, value] = entries[i];
        sb.push('"');
        sb.push(JsonUtility.escapeJson(key));
        sb.push('":');
        appendJsonValue(sb, value, maskHtml);
    }
    sb.push('}');
}

/**
 * THE single artifact encoder. Encodes a built artifact (HtmlJson, Metadata, ...) to its
 * canonical compact JSON byte form. api.json emission and any other producer call this so
 * ConfigClient and ConfigOCI receive byte-identical artifacts (format owned by the loader).
 * Mirrors C# LoaderJsonHelper.EncodeArtifactBytes.
 */
export function encodeArtifactBytes(artifact: JsonObject | null, maskHtml: boolean = false): string {
    const tmp: string[] = [];
    appendDictionaryAsJsonObject(tmp, artifact, maskHtml);
    return tmp.join('');
}

/**
 * Appends a built artifact as an OPAQUE escaped JSON string (its encoded bytes) into an api.json
 * builder, so ConfigClient stores/serves it verbatim like ConfigOCI's .ociartifacts layer.
 * Mirrors C# LoaderJsonHelper.AppendEncodedArtifact.
 */
export function appendEncodedArtifact(sb: string[], artifact: JsonObject | null, maskHtml: boolean = false): void {
    const encoded = encodeArtifactBytes(artifact, maskHtml);
    sb.push('"');
    sb.push(JsonUtility.escapeJson(encoded));
    sb.push('"');
}

// #endregion

// #region Key Utilities

/**
 * Returns keys as array preserving insertion order
 * Keys have numeric prefix (001_, 002_) for ordering
 */
export function getKeysList(keys: string[]): string[] {
    return [...keys]; // Preserve insertion order
}

// #endregion

// #region Value Conversion

/**
 * Converts a value to string representation
 */
export function stringifyJsonValue(value: unknown): string {
    if (value === null || value === undefined) {
        return '';
    }

    if (typeof value === 'string') {
        return value;
    }

    if (typeof value === 'boolean') {
        return value ? 'true' : 'false';
    }

    return (value as object).toString();
}

/**
 * Converts a value to boolean following standard rules
 */
export function convertToBoolean(value: unknown): boolean {
    if (value === null || value === undefined) {
        return false;
    }

    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'string') {
        return value.toLowerCase() === 'true';
    }

    if (typeof value === 'number') {
        return value !== 0;
    }

    return false;
}

// #endregion

// #region Placeholder Utilities

/**
 * Manually splits HTML content by placeholder patterns {{...}}
 */
export function splitByPlaceholders(content: string): string[] {
    const parts: string[] = [];
    let pos = 0;

    while (pos < content.length) {
        const openIndex = content.indexOf('{{', pos);

        if (openIndex === -1) {
            // No more placeholders, add remaining content
            if (pos < content.length) {
                parts.push(content.substring(pos));
            }
            break;
        }

        // Add content before placeholder
        if (openIndex > pos) {
            parts.push(content.substring(pos, openIndex));
        }

        // Find matching }}
        const closeIndex = content.indexOf('}}', openIndex + 2);
        if (closeIndex === -1) {
            // Invalid placeholder, treat rest as content
            parts.push(content.substring(openIndex));
            break;
        }

        // Extract placeholder including {{ and }}
        const placeholder = content.substring(openIndex, closeIndex + 2);

        // Validate placeholder format: {{[#@$/]?[A-Za-z0-9_]+}}
        if (isValidPlaceholder(placeholder)) {
            parts.push(placeholder);
        } else {
            // Not a valid placeholder, treat as content
            parts.push(placeholder);
        }

        pos = closeIndex + 2;
    }

    return parts;
}

/**
 * Validates if a string is a valid placeholder pattern
 */
export function isValidPlaceholder(placeholder: string): boolean {
    if (placeholder.length < 4) return false;
    if (!placeholder.startsWith('{{') || !placeholder.endsWith('}}')) return false;

    // Extract content between {{ and }}
    const inner = placeholder.substring(2, placeholder.length - 2);
    if (inner.length === 0) return false;

    let nameStart = 0;

    // Check for optional prefix: #, @, $, /, ?, ^
    if (inner[0] === '#' || inner[0] === '@' || inner[0] === '$' || inner[0] === '/' || inner[0] === '?' || inner[0] === '^') {
        nameStart = 1;
    }

    // Validate remaining characters are alphanumeric or underscore
    for (let i = nameStart; i < inner.length; i++) {
        const c = inner[i];
        if (!((c >= 'A' && c <= 'Z') || (c >= 'a' && c <= 'z') || (c >= '0' && c <= '9') || c === '_')) {
            return false;
        }
    }

    return inner.length > nameStart; // Must have at least one character after prefix
}

// #endregion

export const LoaderJsonHelper = {
    appendDictionaryAsJsonObject,
    appendJsonValue,
    encodeArtifactBytes,
    appendEncodedArtifact,
    simpleValueToString,
    getKeysList,
    stringifyJsonValue,
    convertToBoolean,
    splitByPlaceholders,
    isValidPlaceholder
};
